import { createContext } from 'react';
import { createDispatchHook, createSelectorHook, createStoreHook, Provider } from 'react-redux';
import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import dashboardDataReducer from './ducks/dashboardDataSlice';
import reportViewReducer from './ducks/reportViewSlice';
import dashboardFiltersReducer, { DASHBOARD_FILTERS_NAMESPACE } from './ducks/dashboardFiltersSlice';
import { dataSourceMetadataNamespace, dataSourceMetadataReducer } from './ducks/dataSourceMetadataSlice';
import { dataSourcePropertiesNamespace, dataSourcePropertiesReducer } from './ducks/dataSourcePropertiesSlice';
import { quickFilterSliceReducer, quickFiltersNamespace } from './ducks/quickFilterSlice';
import userInfoReducer, { userInfoNamespace } from './ducks/userInfoSlice';
import widgetLayoutReducer from './ducks/widgetLayoutSlice';
import dataSourceTopPropertiesReducer from './ducks/dataSourceTopPropertiesSlice';
import dataSourceRecentlyUsedPropertiesReducer from './ducks/dataSourceRecentlyUsedPropertiesSlice';
import { REPORT_DATA_NAMESPACE } from './ducks/reportData/types';
import reportDataSliceReducer from './ducks/reportData/slice';
import { USERS_NAMESPACE } from './ducks/users/types';
import { usersDataReducer } from './ducks/users/slice';
import { teamsDataReducer } from './ducks/teams/slice';
import { TEAMS_DATA_NAMESPACE } from './ducks/teams/types';
import { TEAMS_HIERARCHY_DATA_NAMESPACE } from './ducks/teamHierarchy/types';
import { teamsHierarchyDataReducer } from './ducks/teamHierarchy/slice';
import { OVERRIDDEN_REPORTS_NAMESPACE, overriddenReportsReducer } from './ducks/overriddenReportsSlice/slice';
import { useState } from 'react';
import { applyReduxListeners } from './ducks/dashboardFilterListeners';
import { jsx as _jsx } from "react/jsx-runtime";
const listenerMiddleware = applyReduxListeners(createListenerMiddleware());
export const initializeStore = ({
  preloadedState
}) => {
  return configureStore({
    reducer: {
      dashboardData: dashboardDataReducer,
      reportView: reportViewReducer,
      [DASHBOARD_FILTERS_NAMESPACE]: dashboardFiltersReducer,
      [dataSourceMetadataNamespace]: dataSourceMetadataReducer,
      [dataSourcePropertiesNamespace]: dataSourcePropertiesReducer,
      dataSourceTopProperties: dataSourceTopPropertiesReducer,
      dataSourceRecentlyUsedProperties: dataSourceRecentlyUsedPropertiesReducer,
      [quickFiltersNamespace]: quickFilterSliceReducer,
      [userInfoNamespace]: userInfoReducer,
      widgetLayout: widgetLayoutReducer,
      [REPORT_DATA_NAMESPACE]: reportDataSliceReducer,
      [USERS_NAMESPACE]: usersDataReducer,
      [TEAMS_DATA_NAMESPACE]: teamsDataReducer,
      [TEAMS_HIERARCHY_DATA_NAMESPACE]: teamsHierarchyDataReducer,
      [OVERRIDDEN_REPORTS_NAMESPACE]: overriddenReportsReducer
    },
    middleware: getDefaultMiddleware => getDefaultMiddleware({
      serializableCheck: false
    }).prepend(listenerMiddleware.middleware),
    preloadedState
  });
};
export const StoreContext = /*#__PURE__*/createContext({});

// Export your custom hooks if you wish to use them in other files.
export const usePlatformDashboardStore = createStoreHook(StoreContext);
export const usePlatformDashboardDispatch = createDispatchHook(StoreContext);
export const usePlatformDashboardSelector = createSelectorHook(StoreContext);
export const PlatformDashboardProvider = ({
  preloadedState = {},
  children
}) => {
  const [storeInstance] = useState(initializeStore({
    preloadedState
  }));
  return /*#__PURE__*/_jsx(Provider, {
    context: StoreContext,
    store: storeInstance,
    children: children
  });
};

// https://stackoverflow.com/questions/66315413/type-definitions-for-redux-toolkit-store-with-preloadedstate

// Infer the `RootState` and `AppDispatch` types from the store itself

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}